import type {
  InteractiveElement,
  ButtonActionType,
} from '~/stores/interactiveElements.store';
import { interactiveElementsStore } from '~/stores/interactiveElements.store';
import { streamStore } from '~/stores';
import { triggerStore } from '~/stores/trigger.store';
import type { TriggerFiredPayload } from '~/services/sync/types';
import analyticsService from '~/services/analytics/AnalyticsService';
import { otaLog } from '~/services/ota/OtaDebugLogger';
import { request } from '~/api/request';

// Sintoma debug: botão configurado FIRE_TRIGGER_ONCE/LOOP mas trigger não
// dispara em prod. v1.0.132 confirmou via otaLog que a causa típica é trigger
// SEM ATRIBUIÇÃO AO PLAYER no admin → triggerStore.triggerDefinitions vazio.
// Mantemos UM otaLog.error pra esse caso específico (trigger not found) pra
// diagnóstico futuro — visível com `adb logcat OtaDebug:V "*:S"`.

export function dispatchButtonAction(element: InteractiveElement): void {
  const action = element.actionType;
  if (!action) return;

  console.log(
    `[ActionDispatcher] ${action} — element=${element.id}, label="${element.label}"`,
  );

  trackButtonClick(element);

  switch (action) {
    case 'SKIP_TO_NEXT':
      skipToNext();
      break;
    case 'DISMISS_ELEMENTS':
      interactiveElementsStore.dismissAll();
      break;
    case 'FIRE_TRIGGER_ONCE':
    case 'FIRE_TRIGGER_LOOP':
      fireTrigger(element, action);
      break;
    case 'FIRE_TRIGGER_ALL_DEVICES':
      fireTriggerAllDevices(element);
      break;
  }
}

function skipToNext(): void {
  streamStore.playNext();
}

function fireTrigger(
  element: InteractiveElement,
  action: ButtonActionType,
): void {
  // "Tocar um Conteúdo" (F2) reusa o motor de gatilho: ref_kind='content' aponta
  // pra um conteúdo avulso via content_id (UUID); as demais ações usam trigger_id
  // (id de gatilho/campanha). Ambos vivem no MESMO triggerDefinitions — UUID não
  // colide com id numérico. Vide FEATURE-BOTAO-TOCAR-CONTEUDO.md.
  const isContentRef = element.actionPayload?.ref_kind === 'content';
  const lookupId = isContentRef
    ? element.actionPayload?.content_id
    : element.actionPayload?.trigger_id;
  if (!lookupId) {
    console.warn(
      `[ActionDispatcher] FIRE_TRIGGER without ${isContentRef ? 'content_id' : 'trigger_id'}`,
      element.id,
    );
    return;
  }

  const triggerDef = triggerStore.triggerDefinitions.get(String(lookupId));
  if (!triggerDef) {
    // v1.0.133 — único log nativo mantido: trigger/conteúdo configurado mas não
    // chegou no sync. Causa típica: gatilho não atribuído ao player / conteúdo
    // referenciado ausente. Diagnóstico via `adb logcat OtaDebug:V "*:S"`.
    const availableTriggers = Array.from(
      triggerStore.triggerDefinitions.keys(),
    ).join(',');
    otaLog.error('[ActionDispatcher] Trigger NOT FOUND', {
      requestedTriggerId: lookupId,
      availableTriggers: availableTriggers || '(empty)',
      hint: 'Verifique se o gatilho está atribuído a este player no admin',
    });
    return;
  }

  const mode = action === 'FIRE_TRIGGER_LOOP' ? 'CONTINUOUS' : 'ONCE';

  const contents: TriggerFiredPayload['contents'] = triggerDef.playlist.map(
    (item, i) => ({
      contentId: item.id,
      sequenceOrder: i,
      durationOverride: item.hasDurationOverride ? item.duration : null,
      type: (item.type === 'video' ? 'video' : 'image') as 'video' | 'image',
      url: item.url,
      duration: item.duration,
    }),
  );

  const autoDeactivateSeconds =
    mode !== 'CONTINUOUS'
      ? contents.reduce((sum, c) => sum + c.duration, 0)
      : undefined;

  const payload: TriggerFiredPayload = {
    // Content-trigger não tem id numérico de gatilho (o conteúdo é UUID) →
    // sentinela -1. O playback vem de `contents` (playlist), não do triggerId.
    triggerId: isContentRef ? -1 : Number(lookupId),
    triggerName: triggerDef.name,
    mode,
    contents,
    autoDeactivateSeconds,
  };

  console.log(
    `[ActionDispatcher] Firing ${isContentRef ? 'content' : 'trigger'} ${lookupId} (${triggerDef.name}, mode=${mode}, ${contents.length} items)`,
  );
  triggerStore.handleTriggerFired(payload);
}

/**
 * Dispara o gatilho em TODOS os players configurados (broadcast via server) —
 * ação FIRE_TRIGGER_ALL_DEVICES. Diferente do fireTrigger local, NÃO aplica o
 * gatilho aqui: chama o servidor (POST /api/devices/trigger-fire, autenticado
 * por X-Device-Token) que resolve os players configurados e publica
 * `trigger_fired` via SSE — inclusive de volta pra ESTE device (que também está
 * configurado), então a troca de conteúdo acontece via o mesmo caminho SSE em
 * todos. Mesma ação do "Loop Único" da tela de Gatilho (mode ONCE server-side).
 *
 * Gate de segurança fica no servidor: só dispara se o gatilho estiver
 * configurado neste device (senão 403).
 */
function fireTriggerAllDevices(element: InteractiveElement): void {
  const triggerId = element.actionPayload?.trigger_id;
  if (!triggerId) {
    console.warn(
      '[ActionDispatcher] FIRE_TRIGGER_ALL_DEVICES without trigger_id',
      element.id,
    );
    return;
  }

  console.log(
    `[ActionDispatcher] Broadcasting trigger ${triggerId} to ALL configured devices`,
  );
  request
    .post('/api/devices/trigger-fire', { trigger_id: triggerId })
    .then(() => {
      console.log(
        `[ActionDispatcher] Trigger ${triggerId} broadcast OK (server → SSE trigger_fired)`,
      );
    })
    .catch((err) => {
      otaLog.error('[ActionDispatcher] Broadcast trigger falhou', {
        triggerId: String(triggerId),
        error: String(err),
        hint: 'Verifique se o gatilho está configurado neste device (server retorna 403 se não)',
      });
    });
}

function trackButtonClick(element: InteractiveElement): void {
  analyticsService.trackEvent({
    type: 'button_click' as never,
    data: {
      elementId: element.id,
      contentId: element.contentId,
      actionType: element.actionType ?? undefined,
      label: element.label,
    },
  });
}
